import { useMemo, useState } from 'react'
import '../../suite-help-operacional.css'

type RecordRow = Record<string, unknown>

type SuiteHelpItem = {
  id: number | string
  codigo: string
  ordemServico: string
  assunto: string
  custo: string
  prazo: string
  quemPodeSolicitar: string
  canalSolicitacao: string
  quandoAbrir: string
  sugestaoTexto: string
  observacao: string
  palavrasChave: string[]
}

type Props = {
  records: RecordRow[]
  source: 'online' | 'cache'
  savedAt: string | null
  loading: boolean
  error: string
}

type RankedItem = { item: SuiteHelpItem; score: number; reason: string }

function text(value: unknown) {
  return value == null ? '' : String(value).trim()
}

function arrayText(value: unknown) {
  if (Array.isArray(value)) return value.map((item) => text(item)).filter(Boolean)
  return text(value) ? [text(value)] : []
}

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function words(value: string) {
  return normalize(value).split(/\s+/).filter((word) => word.length >= 2)
}

function toItem(row: RecordRow, index: number): SuiteHelpItem {
  return {
    id: (row.id as number | string | undefined) ?? index,
    codigo: text(row.codigo),
    ordemServico: text(row.ordem_servico),
    assunto: text(row.assunto),
    custo: text(row.custo),
    prazo: text(row.prazo),
    quemPodeSolicitar: text(row.quem_pode_solicitar),
    canalSolicitacao: text(row.canal_solicitacao),
    quandoAbrir: text(row.quando_abrir),
    sugestaoTexto: text(row.sugestao_texto),
    observacao: text(row.observacao_ia),
    palavrasChave: arrayText(row.palavras_chave),
  }
}

function rankItem(item: SuiteHelpItem, query: string): RankedItem | null {
  const needle = normalize(query)
  if (!needle) return { item, score: 0, reason: 'Base oficial' }

  const queryWords = words(query)
  const os = normalize(item.ordemServico)
  const codigo = normalize(item.codigo)
  const assunto = normalize(item.assunto)
  const keywords = normalize(item.palavrasChave.join(' '))
  const quando = normalize(item.quandoAbrir)
  const observacao = normalize(item.observacao)
  const sugestao = normalize(item.sugestaoTexto)
  const meta = normalize([item.custo, item.prazo, item.quemPodeSolicitar, item.canalSolicitacao].join(' '))

  let score = 0
  let reason = ''

  if (needle === os || needle === codigo) {
    score += 1000
    reason = 'OS / código exato'
  } else if (os && os.includes(needle)) {
    score += 700
    reason = 'OS encontrada'
  }

  if (assunto === needle) {
    score += 650
    reason ||= 'Assunto exato'
  } else if (assunto.includes(needle)) {
    score += 430
    reason ||= 'Assunto encontrado'
  }

  if (keywords.includes(needle)) {
    score += 300
    reason ||= 'Palavra-chave encontrada'
  }
  if (quando.includes(needle)) {
    score += 220
    reason ||= 'Regra de abertura relacionada'
  }
  if (observacao.includes(needle)) {
    score += 160
    reason ||= 'Orientação relacionada'
  }
  if (sugestao.includes(needle)) {
    score += 120
    reason ||= 'Texto sugerido relacionado'
  }
  if (meta.includes(needle)) {
    score += 70
    reason ||= 'Informação operacional relacionada'
  }

  const searchable = [os, codigo, assunto, keywords, quando, observacao, sugestao, meta].join(' ')
  let matchedWords = 0
  queryWords.forEach((word) => {
    if (searchable.includes(word)) matchedWords += 1
    if (assunto.includes(word)) score += 55
    else if (keywords.includes(word)) score += 38
    else if (quando.includes(word)) score += 25
    else if (observacao.includes(word)) score += 16
  })
  score += matchedWords * 12

  if (score <= 0 || (queryWords.length >= 2 && matchedWords === 0)) return null
  return { item, score, reason: reason || 'Termos relacionados' }
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

function summary(item: SuiteHelpItem) {
  return [
    `OS ${item.ordemServico} — ${item.assunto}`,
    `Custo: ${item.custo || '—'}`,
    `Prazo: ${item.prazo || '—'}`,
    `Quem pode solicitar: ${item.quemPodeSolicitar || '—'}`,
    `Canal: ${item.canalSolicitacao || '—'}`,
    `Quando abrir: ${item.quandoAbrir || '—'}`,
    item.observacao ? `Orientação: ${item.observacao}` : '',
  ].filter(Boolean).join('\n')
}

const QUICK_SEARCHES = [
  '101005',
  'falta de água',
  'levantamento rede de água',
  'TIL',
  'vazamento de água',
  'religação',
]

export function SuiteHelpOperational({ records, source, savedAt, loading, error }: Props) {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<number | string | null>(null)
  const [message, setMessage] = useState('')

  const items = useMemo(() => records.map(toItem), [records])
  const ranked = useMemo(() => {
    const result = items
      .map((item) => rankItem(item, query))
      .filter((item): item is RankedItem => Boolean(item))
      .sort((a, b) => b.score - a.score || a.item.ordemServico.localeCompare(b.item.ordemServico, 'pt-BR'))
    return result.slice(0, 12)
  }, [items, query])

  const selected = useMemo(() => {
    const explicit = selectedId == null ? null : items.find((item) => item.id === selectedId) ?? null
    if (explicit && (!query.trim() || ranked.some((entry) => entry.item.id === explicit.id))) return explicit
    return ranked[0]?.item ?? null
  }, [items, ranked, query, selectedId])

  async function handleCopy(label: string, value: string) {
    const ok = await copyText(value)
    setMessage(ok ? `${label} copiado.` : `Não foi possível copiar ${label.toLocaleLowerCase('pt-BR')}.`)
    window.setTimeout(() => setMessage(''), 1800)
  }

  function setSearch(value: string) {
    setQuery(value)
    setSelectedId(null)
    setMessage('')
  }

  return (
    <section className="suite-help-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL DE OS</p>
          <h2>Suite Help</h2>
          <p className="muted">Pesquise por ordem de serviço, assunto, situação do cliente ou palavras-chave e consulte a orientação oficial.</p>
        </div>
        <span className={`status-chip ${source === 'cache' ? 'warning' : ''}`}>{source === 'cache' ? 'Contingência' : 'Base publicada'}</span>
      </div>

      <div className="suite-help-layout">
        <aside className="suite-help-search-card">
          <label>
            OS, assunto ou situação do atendimento
            <input
              type="search"
              value={query}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Ex.: 101005, falta de água, TIL..."
              autoFocus
            />
          </label>
          <div className="suite-help-search-actions">
            <button className="secondary-button" type="button" onClick={() => setSearch('')}>Limpar</button>
            <span>{items.length} OS disponíveis</span>
          </div>

          <div className="suite-help-quick">
            <strong>Buscas rápidas</strong>
            <div>{QUICK_SEARCHES.map((value) => <button type="button" key={value} onClick={() => setSearch(value)}>{value}</button>)}</div>
          </div>

          {source === 'cache' && savedAt && <p className="suite-help-cache">Última sincronização: {new Date(savedAt).toLocaleString('pt-BR')}</p>}

          <div className="suite-help-result-list">
            {ranked.map((entry, index) => (
              <button
                type="button"
                key={entry.item.id}
                className={selected?.id === entry.item.id ? 'active' : ''}
                onClick={() => setSelectedId(entry.item.id)}
              >
                <span><strong>{entry.item.ordemServico}</strong>{index === 0 && query.trim() && <em>Mais provável</em>}</span>
                <b>{entry.item.assunto}</b>
                <small>{query.trim() ? entry.reason : entry.item.prazo}</small>
              </button>
            ))}
            {!loading && !error && query.trim() && ranked.length === 0 && <div className="empty-state">Nenhuma OS encontrada para essa busca.</div>}
          </div>
        </aside>

        <div className="suite-help-detail-area">
          {loading && <div className="empty-state">Carregando Suite Help...</div>}
          {error && <div className="notice error">{error}</div>}
          {!loading && !error && !selected && <div className="empty-state">Digite uma OS, assunto ou situação do cliente para consultar.</div>}

          {!loading && !error && selected && (
            <article className="suite-help-detail-card">
              <header>
                <div>
                  <p className="eyebrow">ORDEM DE SERVIÇO</p>
                  <div className="suite-help-title-row"><strong>{selected.ordemServico}</strong><h3>{selected.assunto}</h3></div>
                  {selected.codigo && selected.codigo !== selected.ordemServico && <small>Código da base: {selected.codigo}</small>}
                </div>
                {query.trim() && ranked[0]?.item.id === selected.id && <span className="status-chip">Mais provável</span>}
              </header>

              <div className="suite-help-metrics">
                <article><span>Custo</span><strong>{selected.custo || '—'}</strong></article>
                <article><span>Prazo</span><strong>{selected.prazo || '—'}</strong></article>
                <article><span>Quem pode solicitar</span><strong>{selected.quemPodeSolicitar || '—'}</strong></article>
                <article><span>Canal</span><strong>{selected.canalSolicitacao || '—'}</strong></article>
              </div>

              <section className="suite-help-info primary">
                <span>Quando abrir</span>
                <p>{selected.quandoAbrir || 'Orientação não informada na base.'}</p>
              </section>

              {selected.observacao && (
                <section className="suite-help-info warning">
                  <span>Atenção operacional</span>
                  <p>{selected.observacao}</p>
                </section>
              )}

              <section className="suite-help-text-block">
                <div><span>Texto sugerido para registro</span><button type="button" onClick={() => void handleCopy('Texto sugerido', selected.sugestaoTexto)}>Copiar texto</button></div>
                <pre>{selected.sugestaoTexto || 'Nenhum texto sugerido cadastrado.'}</pre>
              </section>

              {selected.palavrasChave.length > 0 && (
                <div className="suite-help-keywords">
                  <strong>Palavras-chave</strong>
                  <div>{selected.palavrasChave.slice(0, 18).map((word, index) => <span key={`${word}-${index}`}>{word}</span>)}</div>
                </div>
              )}

              <div className="suite-help-actions">
                <button className="primary-button compact" type="button" onClick={() => void handleCopy('Resumo operacional', summary(selected))}>Copiar resumo operacional</button>
              </div>
              {message && <div className="tool-status ok suite-help-copy-message">{message}</div>}
            </article>
          )}
        </div>
      </div>
    </section>
  )
}
